import React from 'react';
/**
 * PracticeSession full-page states — Loading, Error, Empty variants.
 *
 * Extracted from PracticeSession.tsx in s69 to reduce its size from 978→~680 lines.
 * All components are pure presentational (no state management).
 */
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { AlertCircle, BookmarkCheck } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
/*
 `DjayIdentity` ontbrak hier terwijl het bestand hem op twee plekken rendert
 (de laadspinner en de lege staat) — de import stond nog op het rauwe
 `DjayAvatar` van vóór de Identity-migratie, dat inmiddels nergens meer in dit
 bestand gebruikt wordt. Gevolg: elke oefensessie viel bij het laden om met
 "DjayIdentity is not defined", in álle vakken.

 Dat het een runtime-crash werd en geen compileerfout, komt doordat een
 ongedefinieerde JSX-verwijzing pas bij het renderen wordt opgezocht. De
 lintguard uit de mascotte-audit kijkt naar wat er wél staat (`<DjayAvatar>`),
 niet naar wat er ontbreekt — precies de blinde vlek waar deze in viel.
 */
import { DjayIdentity, DjayToetsAvatar, getDjayThinkingMessage } from '../../mascot';
// ── Loading Spinner (shared animation) ───────────────────────
// F-A11Y-4: respect prefers-reduced-motion. The infinite scale/rotate loop
// is gated on useReducedMotion() so users with motion-sensitivity (or who
// have set the OS-level reduced-motion preference) see a still Djay rather
// than a continuously breathing one.
function LoadingSpinner() {
  const reduce = useReducedMotion();
  return <motion.div animate={reduce ? undefined : {
    scale: [1, 1.05, 1],
    rotate: [0, 3, -3, 0]
  }} transition={reduce ? undefined : {
    duration: 2.5,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className="mx-auto mb-6" data-id="element-11706" data-mp="3nrbe99opinsm">
      
      {/* @djay-slot: practice-loading */}
      <DjayIdentity size="xl" emotion="thinking" data-id="element-11707" data-mp="1qukq73blb0o1" />
    </motion.div>;
}
// ── Loading State ────────────────────────────────────────────
export function SessionLoadingState() {
  return <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50" data-id="element-11708" data-mp="1keu2d0iipc5b">
      <div className="text-center" data-id="element-11709" data-mp="1fi3mad6pqgjw">
        <LoadingSpinner data-id="element-11710" data-mp="1fj589cp9onge" />
        <h3 className="text-2xl font-black text-slate-800 mb-3" data-id="element-11711" data-mp="1bg8m5c0iq97p">
          Even geduld...
        </h3>
        <p className="text-slate-500 font-medium text-lg" data-id="element-11712" data-mp="286iwmmewgaow">
          {getDjayThinkingMessage().text}
        </p>
      </div>
    </div>;
}
// ── Error State (generation failed) ──────────────────────────
export function SessionErrorState({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11713" data-mp="1uekzl9ygq593">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-11714" data-mp="1ucry092pib7y">
        <div className="text-6xl mb-6" data-id="element-11715" data-mp="2m5imfxe5evmn">😕</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11716" data-mp="2m3o0qgv1yzhi">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11717" data-mp="dm7mjxj29qtk">
          De oefensessie kon niet worden geladen.
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-11718" data-mp="hmrhqvk2qkd2">
          
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State (fallback) ───────────────────────────────────
export function SessionEmptyFallback({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11719" data-mp="wiqp23zd0gh5">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100 border-2 border-indigo-50" data-id="element-11720" data-mp="11clezp5k5roo">
        <div className="text-6xl mb-6" data-id="element-11721" data-mp="1acqdtaworhr8">🎯</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11722" data-mp="1ae1ire1vavjx">
          Geen vragen gevonden
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11723" data-mp="1f98enzowavya">
          Er zijn momenteel geen vragen beschikbaar voor deze selectie.
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-11724" data-mp="wozspjws6niy">
          
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State: No Content ──────────────────────────────────
export function SessionEmptyNoContent({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11725" data-mp="dqwxgndn3ucp">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-amber-100 border-2 border-amber-50" data-id="element-11726" data-mp="dsztpp5dyjpv">
        <div className="text-6xl mb-6" data-id="element-11727" data-mp="nojq0cnv3kf5">📭</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11728" data-mp="nnrut2y6fope">
          Geen oefenstof beschikbaar
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11729" data-mp="1z48cz4148jfy">
          Er is nog geen oefenstof beschikbaar voor dit onderwerp. Probeer een
          ander onderwerp!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-11730" data-mp="2bmuadlc8bv96">
          
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State: All Mastered ────────────────────────────────
export function SessionEmptyAllMastered({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11731" data-mp="2pfju1j4aco34">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-emerald-100 border-2 border-emerald-50" data-id="element-11732" data-mp="2ucaai7k05lp2">
        <div className="text-6xl mb-6" data-id="element-11733" data-mp="2gjugtxczr00t">🏆</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11734" data-mp="2kmqrn1wahru7">
          Alles onder de knie!
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11735" data-mp="38cbdptnqp57p">
          Je hebt alle beschikbare vragen voor dit onderwerp al goed beantwoord.
          Geweldig gedaan!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-emerald-200" data-id="element-11736" data-mp="3myiix4ew3e8t">
          
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Safety Error (question undefined but session has questions) ─
export function SessionSafetyError({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11737" data-mp="340xqxogtvzjz">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-11738" data-mp="342sdf77qzzq6">
        <div className="text-6xl mb-6" data-id="element-11739" data-mp="2v844n72yrzwm">🤔</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11740" data-mp="2v8vs1umeqrbt">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11741" data-mp="2q7q1a2cb5e4x">
          We konden de vraag niet laden. Probeer het nog eens!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-11742" data-mp="2gco7uub01pwj">
          
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Shared modal scaffolding ─────────────────────────────────
// Both exit modals (practice + assessment) share an identical backdrop +
// spring-in card frame. Extracted to keep the modals themselves focused on
// copy / visuals / call-to-action.
function ExitModalFrame({
  show,
  onDismiss,
  borderTone,
  labelledBy,
  children
}: {
  show: boolean;
  onDismiss: () => void;
  borderTone: 'indigo' | 'amber';
  labelledBy: string;
  children: React.ReactNode;
}) {
  const focusTrapRef = useFocusTrap(show);
  const borderClass = borderTone === 'indigo' ? 'border-indigo-100' : 'border-amber-100';
  return <AnimatePresence data-id="element-11743" data-mp="227e77gfdja42">
      {show && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={onDismiss} aria-hidden="true" data-id="element-11744" data-mp="14qgxyad2luqv" />
        
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        type: 'spring',
        bounce: 0.2,
        duration: 0.4
      }} ref={focusTrapRef} className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby={labelledBy} data-id="element-11745" data-mp="2nwikxxaqv1kt">
          
            <Card className={`max-w-md w-full p-8 shadow-2xl border-2 ${borderClass} bg-white rounded-3xl`} data-id="element-11746" data-mp="32shqb06gynm9">
            
              {children}
            </Card>
          </motion.div>
        </>}
    </AnimatePresence>;
}
// ── Practice Exit Modal ──────────────────────────────────────
// Used during a free-practice / lab / fluency session. Copy is intentionally
// reassuring: practice answers are persisted per-question via the onAnswer
// callback (mastery, XP, daily streak all update on submit), so stopping
// mid-session does NOT lose work — only the end-of-session summary screen.
// This modal is distinct from the assessment ExitConfirmModal below, which
// uses the stricter "incomplete attempt" framing required by 'toets' mode.
export function PracticeExitModal({
  show,
  answeredCount,
  totalQuestions,
  onContinue,
  onStop
}: {
  show: boolean;
  answeredCount: number;
  totalQuestions: number;
  onContinue: () => void;
  onStop: () => void;
}) {
  return <ExitModalFrame show={show} onDismiss={onContinue} borderTone="indigo" labelledBy="practice-exit-title" data-id="element-11747" data-mp="3q8tiukdjnpy5">
      
      <div className="text-center" data-id="element-11748" data-mp="3chy26lwt04k0">
        <div className="mx-auto mb-4 flex justify-center" data-id="element-11749" data-mp="3cesenb3tpaqe">
          <DjayIdentity size="lg" emotion="thinking" animate data-id="element-11750" data-mp="3hh1aqgbzsswq" />
        </div>
        <h2 id="practice-exit-title" className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-11751" data-mp="32i782lc5nrkg">
          
          Stoppen met oefenen?
        </h2>
        <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-11752" data-mp="37jwl26ixufb6">
          Geen probleem — je antwoorden blijven bewaard. Als je later weer
          oefent, start je een nieuwe sessie.
        </p>
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-6 text-left" data-id="element-11753" data-mp="2hmsdu2fpn3ks">
          <div className="flex items-start gap-3" data-id="element-11754" data-mp="22tp7kr7kkc9d">
            <BookmarkCheck className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-11755" data-mp="27pw4juhzmdf6" />
            <div className="text-sm text-indigo-900 font-medium leading-relaxed" data-id="element-11756" data-mp="2vfqf25eu3pw9">
              {/* Stond hier als onvoorwaardelijke belofte ("zijn al
                   opgeslagen"), terwijl een schrijfactie kan mislukken — de
                   StorageWarningBanner bestaat juist daarvoor. Een kind dat
                   stopt op basis van die belofte, verliest dan werk. De tekst
                   zegt nu wat er meetelt, zonder een garantie die we niet
                   kunnen geven. */}
              <p className="font-bold mb-0.5" data-id="element-11757" data-mp="3j6dztr9uma08">Je werk telt gewoon mee</p>
              <p className="text-indigo-800" data-id="element-11758" data-mp="3j7p5jwmutrtz">
                {answeredCount} van de {totalQuestions} vragen tellen mee voor
                je mastery, XP en streak.
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-11759" data-mp="1hh6pz0mupy3d">
          <Button onClick={onContinue} variant="primary" size="lg" fullWidth data-id="element-11760" data-mp="1hg5g2zhknhn8">
            Doorgaan met oefenen
          </Button>
          <Button onClick={onStop} variant="secondary" size="lg" fullWidth className="text-slate-700" data-id="element-11761" data-mp="1cjy8z3dfw03p">
            
            Stop voor nu
          </Button>
        </div>
      </div>
    </ExitModalFrame>;
}
// ── Exit Confirmation Modal (assessment / toets mode) ────────
// Stricter framing for graded 'toets' sessions where stopping mid-session
// produces an explicit "incomplete" attempt record. Visual now uses
// DjayToetsAvatar (test-themed Djay) instead of the generic 🤔 emoji so the
// modal matches the rest of the assessment shell's visual language.
export function ExitConfirmModal({
  show,
  currentIndex,
  totalQuestions,
  onContinue,
  onStop
}: {
  show: boolean;
  currentIndex: number;
  totalQuestions: number;
  onContinue: () => void;
  onStop: () => void;
}) {
  return <ExitModalFrame show={show} onDismiss={onContinue} borderTone="amber" labelledBy="assessment-exit-title" data-id="element-11762" data-mp="16wjacq4erkh8">
      
      <div className="text-center" data-id="element-11763" data-mp="1fv4vmt19ldlp">
        <div className="mx-auto mb-4 flex justify-center" data-id="element-11764" data-mp="1fu3hgfoycazr">
          <DjayToetsAvatar domain="toets" size="lg" animate data-id="element-11765" data-mp="j4uifavi0y0t" />
        </div>
        <h2 id="assessment-exit-title" className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-11766" data-mp="j2rgptkzhgg9">
          
          Toets stoppen?
        </h2>
        <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-11767" data-mp="s4hintchrxsa">
          Weet je zeker dat je wilt stoppen? Je voortgang wordt opgeslagen als
          onvolledig.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left" data-id="element-11768" data-mp="ew9ksjvn0w4">
          <div className="flex items-start gap-3" data-id="element-11769" data-mp="4fg55rosxwg5">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-11770" data-mp="4e52xwb08wr5" />
            <p className="text-sm text-amber-800 font-medium" data-id="element-11771" data-mp="3t0jnb0l2a03x">
              Je hebt {currentIndex + 1} van de {totalQuestions} vragen
              beantwoord. Als je stopt, kun je later opnieuw beginnen.
            </p>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-11772" data-mp="3f21y0yficfjw">
          <Button onClick={onContinue} variant="primary" size="lg" fullWidth data-id="element-11773" data-mp="3f10fk8w5wqs5">
            Doorgaan met toets
          </Button>
          <Button onClick={onStop} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-11774" data-mp="3a49ypjcj9r5o">
            
            Stoppen
          </Button>
        </div>
      </div>
    </ExitModalFrame>;
}